import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SignedDeclarationUpload = ({ onFileUpload, uploadedFile, onRemoveFile, registrationId, onUploadComplete, onUploadError, viewMode = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleFileSelection = async (file) => {
    if (file?.type !== 'application/pdf') {
      alert('Please upload a PDF file only');
      return;
    }

    if (file?.size > 15 * 1024 * 1024) {
      alert('File size must be less than 15MB');
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    if (!registrationId) {
      alert('Registration ID not found. Please refresh the page and try again.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Import registration service dynamically
      const { registrationService } = await import('../../../services/registrationService');

      // Simulate progress while uploading
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Upload to Supabase
      const result = await registrationService.uploadSignedDeclaration(registrationId, file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Call the callback with file info
      onFileUpload({
        name: file?.name,
        size: file?.size,
        uploadDate: new Date().toLocaleDateString('en-IN'),
        url: result.fileUrl,
        filePath: result.filePath
      });

      // Notify parent of successful upload
      if (onUploadComplete) {
        onUploadComplete(result);
      }
    } catch (error) {
      console.error('Error uploading declaration:', error);
      setUploadProgress(0);
      
      if (onUploadError) {
        onUploadError(error);
      } else {
        alert(`Failed to upload declaration: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(2) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(2) + ' MB';
  };

  const handleDownload = () => {
    if (uploadedFile?.url) {
      window.open(uploadedFile.url, '_blank');
    }
  };

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name={viewMode ? "FileText" : "Upload"} size={20} color="var(--color-primary)" />
        {viewMode ? 'Signed Declaration' : 'Upload Signed Declaration'}
      </h3>
      {viewMode ? (
        /* View Mode - Display uploaded file */
        uploadedFile ? (
          <div className="border border-success/20 bg-success/5 rounded-lg p-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="FileCheck" size={24} color="var(--color-success)" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{uploadedFile?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {uploadedFile?.size ? formatFileSize(uploadedFile.size) : 'Unknown size'} • Uploaded on {uploadedFile?.uploadDate}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-success mb-3">
                  <Icon name="CheckCircle" size={16} />
                  <span>Successfully uploaded</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={handleDownload}
                >
                  Download Declaration
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="FileText" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No signed declaration uploaded</p>
          </div>
        )
      ) : (
        <>
          {/* Instructions */}
          <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="AlertCircle" size={18} color="var(--color-warning)" />
              Important Instructions
            </h4>
            <ol className="text-sm text-foreground space-y-1 list-decimal list-inside">
              <li>Download the declaration PDF using the button above</li>
              <li>Print the declaration on school letterhead (if available)</li>
              <li>Sign the declaration as coach and obtain principal's signature with school stamp</li>
              <li>Scan the signed document as a PDF file</li>
              <li>Upload the scanned PDF (maximum size: 15MB)</li>
            </ol>
          </div>
          {!uploadedFile ? (
        <>
          {/* Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
              isDragging
                ? 'border-primary bg-primary/5 scale-102' :'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileInputChange}
              className="hidden"
            />

            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="FileUp" size={32} color="var(--color-primary)" />
              </div>

              {isUploading ? (
                <div className="w-full max-w-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">Uploading...</span>
                    <span className="text-sm font-medium text-primary">{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-base font-medium text-foreground mb-1">
                      Drag and drop your signed declaration here
                    </p>
                    <p className="text-sm text-muted-foreground">or</p>
                  </div>

                  <Button
                    variant="outline"
                    iconName="FolderOpen"
                    iconPosition="left"
                    onClick={handleBrowseClick}
                  >
                    Browse Files
                  </Button>

                  <p className="text-xs text-muted-foreground">
                    Supported format: PDF (Max size: 15MB)
                  </p>
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Uploaded File Display */
        (<div className="border border-success/20 bg-success/5 rounded-lg p-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="FileCheck" size={24} color="var(--color-success)" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{uploadedFile?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(uploadedFile?.size)} • Uploaded on {uploadedFile?.uploadDate}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={onRemoveFile}
                  aria-label="Remove file"
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-success">
                <Icon name="CheckCircle" size={16} />
                <span>Successfully uploaded</span>
              </div>
            </div>
          </div>
        </div>)
          )}
          {/* Requirements Note */}
          {!viewMode && (
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground flex items-start gap-2">
                <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  The uploaded declaration must contain both coach and principal signatures along with the school stamp. 
                  Incomplete declarations will be rejected during verification.
                </span>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SignedDeclarationUpload;